<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="2" />
    <h4>Reporte de Trámites y Planillas por Servicio</h4>
    <hr />

    <!-- Instrucciones -->
    <div class="alert alert-info py-2 px-3 mb-3" role="alert">
      <strong>Instrucciones:</strong>
      Seleccione un rango rápido o ingrese las fechas manualmente y presione
      <strong>Consultar</strong>.
    </div>

    <!-- Filtros -->
    <div class="container mb-4">
      <div class="row g-3">
        <!-- Botones de rango rápido -->
        <div class="col-12 d-flex flex-wrap gap-2">
          <button class="btn btn-outline-primary   btn-sm" @click="setToday">Hoy</button>
          <button class="btn btn-outline-danger    btn-sm" @click="setThisWeek">Semana Actual</button>
          <button class="btn btn-outline-secondary btn-sm" @click="setThisMonth">Mes Actual</button>
          <button class="btn btn-outline-success   btn-sm" @click="setThisQuarter">Trimestre Actual</button>
          <button class="btn btn-outline-info      btn-sm" @click="setThisSemester">Semestre Actual</button>
          <button class="btn btn-outline-warning   btn-sm" @click="setThisYear">Año Actual</button>
        </div>

        <!-- Desde -->
        <div class="col-auto">
          <label class="form-label fw-bold">Desde</label>
          <input type="date" class="form-control" v-model="fechaDesde" />
        </div>

        <!-- Hasta -->
        <div class="col-auto">
          <label class="form-label fw-bold">Hasta</label>
          <input type="date" class="form-control" v-model="fechaHasta" />
        </div>

        <!-- Acciones -->
        <div class="col-auto d-flex align-items-end gap-2">
          <button
            class="btn btn-dark"
            @click="generarReporte"
            :disabled="!fechaDesde || !fechaHasta"
          >
            <i class="material-icons me-1" style="font-size:18px;vertical-align:middle">search</i>
            Consultar
          </button>
          <button
            class="btn btn-outline-success"
            @click="exportToCSV"
            :disabled="datosReporte.length === 0"
          >
            <i class="material-icons me-1" style="font-size:18px;vertical-align:middle">download</i>
            Exportar CSV
          </button>
        </div>
      </div>
    </div>

    <!-- Contenido -->
    <div v-if="datosReporte.length > 0">

      <!-- Nav tabs Bootstrap (preparado para cuando se activen los gráficos) -->
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: showTabla }"
            @click="showTablaSection"
            type="button"
          >
            Tabla
          </button>
        </li>
        <!-- Descomentar cuando se activen los gráficos:
        <li class="nav-item">
          <button class="nav-link" :class="{ active: showTramites }" @click="showTramitesGraph" type="button">
            Gráfico Trámites
          </button>
        </li>
        <li class="nav-item">
          <button class="nav-link" :class="{ active: showPlanillas }" @click="showPlanillasGraph" type="button">
            Gráfico Planillas
          </button>
        </li>
        -->
      </ul>

      <!-- ── Tab Tabla ── -->
      <div v-if="showTabla">
        <div class="table-responsive">
          <table class="table table-hover table-bordered table-sm">
            <thead class="table-dark">
              <tr>
                <th class="th-sort" @click="ordenarPor('servicioNombre')">
                  Servicio <span>{{ iconOrden('servicioNombre') }}</span>
                </th>
                <th class="th-sort text-end" @click="ordenarPor('numeroTramites')">
                  Número de Trámites <span>{{ iconOrden('numeroTramites') }}</span>
                </th>
                <th class="th-sort text-end" @click="ordenarPor('numeroPlanillas')">
                  Número de Planillas <span>{{ iconOrden('numeroPlanillas') }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in datosOrdenados" :key="index">
                <td>{{ item.servicioNombre }}</td>
                <td class="text-end">{{ item.numeroTramites }}</td>
                <td class="text-end">{{ item.numeroPlanillas }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="table-secondary fw-bold">
                <td>Total</td>
                <td class="text-end">{{ totalTramites }}</td>
                <td class="text-end">{{ totalPlanillas }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Gráfico Trámites (desactivado) -->
      <!--
      <div v-if="showTramites">
        <apexchart type="bar" :options="tramitesChartOptions" :series="tramitesChartSeries" height="400" />
      </div>
      <div v-if="showPlanillas">
        <apexchart type="bar" :options="planillasChartOptions" :series="planillasChartSeries" height="400" />
      </div>
      -->

    </div>

    <!-- Sin resultados -->
    <div v-if="sinResultados" class="alert alert-warning mt-3" role="alert">
      No se recuperaron registros.
    </div>

    <Footer />
  </div>
</template>

<script>
import { ref, computed } from "vue";
import Layout from "@/components/Layout.vue";
import Footer from "@/components/Footer.vue";
import api from "@/services/api";
import {
  getToday,
  getThisWeek,
  getThisMonth,
  getThisQuarter,
  getThisSemester,
  getThisYear,
} from "@/modules/queryDateUtils.js";

export default {
  components: { Layout, Footer },
  data() {
    return {
      showTramites:  false,
      showPlanillas: false,
      showTabla:     true,
    };
  },
  computed: {
    totalTramites() {
      return this.datosReporte.reduce((sum, i) => sum + i.numeroTramites, 0);
    },
    totalPlanillas() {
      return this.datosReporte.reduce((sum, i) => sum + i.numeroPlanillas, 0);
    },
  },
  setup() {
    const fechaDesde   = ref("");
    const fechaHasta   = ref("");
    const datosReporte = ref([]);
    const sinResultados = ref(false);

    // ── Ordenamiento ───────────────────────────────────────
    const campoOrden  = ref("servicioNombre");
    const ascendente  = ref(true);

    const datosOrdenados = computed(() => {
      const campo = campoOrden.value;
      return [...datosReporte.value].sort((a, b) => {
        let va = a[campo] ?? "";
        let vb = b[campo] ?? "";
        if (campo === "numeroTramites" || campo === "numeroPlanillas") {
          return ascendente.value ? va - vb : vb - va;
        }
        const cmp = String(va).localeCompare(String(vb), "es");
        return ascendente.value ? cmp : -cmp;
      });
    });

    const ordenarPor = (campo) => {
      if (campoOrden.value === campo) {
        ascendente.value = !ascendente.value;
      } else {
        campoOrden.value = campo;
        ascendente.value = true;
      }
    };

    const iconOrden = (campo) => {
      if (campoOrden.value !== campo) return "⇅";
      return ascendente.value ? "↑" : "↓";
    };

    // ── Fetch ──────────────────────────────────────────────
    const generarReporte = async () => {
      if (!fechaDesde.value || !fechaHasta.value) return;
      sinResultados.value = false;
      try {
        const response = await api.get("/vwtramites/servicio-estadisticas", {
          params: {
            idEmpresa:  window.sessionStorage.getItem("idEmpresa"),
            fechaDesde: fechaDesde.value,
            fechaHasta: fechaHasta.value,
          },
        });
        datosReporte.value  = Array.isArray(response.data) ? response.data : [];
        sinResultados.value = datosReporte.value.length === 0;
      } catch (error) {
        console.error("Error al obtener los datos:", error);
        sinResultados.value = true;
      }
    };

    // ── Rangos rápidos ─────────────────────────────────────
    const setDateRange = (fn) => {
      const dates      = fn();
      fechaDesde.value = dates.fechaDesde;
      fechaHasta.value = dates.fechaHasta;
      generarReporte();
    };
    const setToday        = () => setDateRange(getToday);
    const setThisWeek     = () => setDateRange(getThisWeek);
    const setThisMonth    = () => setDateRange(getThisMonth);
    const setThisQuarter  = () => setDateRange(getThisQuarter);
    const setThisSemester = () => setDateRange(getThisSemester);
    const setThisYear     = () => setDateRange(getThisYear);

    // ── Exportar CSV (separador ";") ───────────────────────
    const exportToCSV = () => {
      const escape = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val).replace(/"/g, '""');
        return str.includes(";") || str.includes("\n") ? `"${str}"` : str;
      };
      const totalT = datosReporte.value.reduce((s, i) => s + i.numeroTramites,  0);
      const totalP = datosReporte.value.reduce((s, i) => s + i.numeroPlanillas, 0);
      const headers = ["Servicio", "Número de Trámites", "Número de Planillas"];
      const rows = [
        headers.join(";"),
        ...datosReporte.value.map((item) =>
          [item.servicioNombre, item.numeroTramites, item.numeroPlanillas]
            .map(escape)
            .join(";")
        ),
        ["Total", totalT, totalP].map(escape).join(";"),
      ];
      const bom  = "\uFEFF";
      const blob = new Blob([bom + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `reporte_tramites_planillas_por_servicio_${fechaDesde.value}_${fechaHasta.value}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    return {
      fechaDesde,
      fechaHasta,
      datosReporte,
      datosOrdenados,
      sinResultados,
      generarReporte,
      ordenarPor,
      iconOrden,
      setToday,
      setThisWeek,
      setThisMonth,
      setThisQuarter,
      setThisSemester,
      setThisYear,
      exportToCSV,
    };
  },
  mounted() {
    this.$refs.layoutRef.toggleProcesos();
    this.$refs.layoutRef.toggleReportes();
    this.$refs.layoutRef.toggleParametros();
    this.$refs.layoutRef.toggleAyuda();
  },
  methods: {
    showTramitesGraph() {
      this.showTramites  = true;
      this.showPlanillas = false;
      this.showTabla     = false;
    },
    showPlanillasGraph() {
      this.showTramites  = false;
      this.showPlanillas = true;
      this.showTabla     = false;
    },
    showTablaSection() {
      this.showTramites  = false;
      this.showPlanillas = false;
      this.showTabla     = true;
    },
  },
};
</script>

<style scoped>
.th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.th-sort:hover {
  background-color: #3d5166;
}
.table-hover tbody tr {
  cursor: pointer;
}
</style>